// @ts-check

/**
 * @typedef {Object} MercadoPagoBankItem
 * @property {string} description - The bank name.
 * @property {string} id - The bank's id.
 */

/**
 * Factory for the MercadoPago engine singleton. Holds the active SDK instance
 * and public key, and exposes tokenization/lookup helpers.
 * @returns {object} The MercadoPago engine API.
 */
const MercadoPago = () => {
  let mpPublicKey = '';
  let mercadoPagoInstance;

  /**
   * Creates (or re-creates) the MercadoPago SDK instance for a public key.
   * Re-initializing with the same key is a no-op: the SDK instance is expensive
   * and this is called again per purchase to select the company's public key
   * (income separation). Only rebuilds when the key actually changes.
   * @param {string} [publicKey] - MercadoPago public key of the account to tokenize against.
   * @returns {void}
   */
  const initialize = (publicKey = '') => {
    if (publicKey === mpPublicKey && mercadoPagoInstance) return;
    // @ts-ignore - MercadoPago is a global variable loaded by the script
    mercadoPagoInstance = new window.MercadoPago(publicKey);
    mpPublicKey = publicKey;
  };

  /**
   * Returns the public key the current SDK instance was initialized with.
   * @returns {string} The active MercadoPago public key.
   */
  const getPublicKey = () => mpPublicKey;

  /**
   * Fetches the list of banks available for PSE payments
   * @async
   * @function fetchPseBanksList
   * @returns {Promise<MercadoPagoBankItem[]>}
   * @throws {Error} if the request fails
   * @example
   * ```js
   * const banks = await mercadoPago.fetchBankList();
   * ```
   */
  const fetchPseBanksList = async () => {
    const endpoint = `https://api.mercadopago.com/v1/payment_methods?public_key=${mpPublicKey}`;
    const response = await fetch(endpoint);
    const allPaymentMethods = await response.json();
    const psePaymentMethod = allPaymentMethods.find((paymentMethod) => paymentMethod.id === 'pse');
    if (!psePaymentMethod) throw new Error('PSE payment method not available');
    const pseBanks = psePaymentMethod.financial_institutions;
    return pseBanks;
  };

  /**
   * Resolves the card payment method (brand, issuer) for a card BIN.
   * @param {object} params - Lookup params.
   * @param {string} params.bin - The card's BIN (first digits).
   * @returns {Promise<object>} The first matching payment method.
   */
  const getCardPaymentMethod = async ({ bin }) => {
    if (!mercadoPagoInstance) {
      throw new Error('La instancia de Mercado Pago no ha sido creada');
    }
    const { results } = await mercadoPagoInstance.getPaymentMethods({
      bin,
    });
    return results[0];
  };

  /**
   * Returns the current MercadoPago SDK instance.
   * @returns {object} The SDK instance.
   * @throws {Error} if the instance has not been created yet.
   */
  const getInstance = () => {
    if (!mercadoPagoInstance) {
      throw new Error('La instancia de Mercado Pago no ha sido creada');
    }
    return mercadoPagoInstance;
  };

  return {
    initialize,
    getPublicKey,
    fetchPseBanksList,
    getInstance,
    getCardPaymentMethod,
  };
};

const mercadoPago = MercadoPago();

export default mercadoPago;
